import { useEffect, useRef, useState } from "react";
import { useParams, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { ArticleMasthead } from "@/components/blog/article-masthead";
import { useSEO } from "@/lib/seo-utils";
import type { LegalDocument } from "@/components/legal/house-rules-display";
import { ShareWaiverLink, StayReturnLink, StickySignBar } from "@/components/legal/share-waiver-link";

/**
 * Per-adult signing page for the Water Activity Waiver.
 *
 * Structured around the validity conditions in HRS s 663-1.54(b), because a
 * release that misses them is void by the statute's own terms rather than just
 * weak:
 *
 *   - The inherent-risk disclosure must come FIRST. The signing controls stay
 *     disabled until the disclosure panel has actually been scrolled to the end,
 *     so "it was on the page somewhere" is not the story we would have to tell.
 *   - Physical ability and safety instruction are separate affirmations, not
 *     folded into one blanket "I agree", because they are separate statutory
 *     requirements and the record should show each was put to the signer.
 *
 * One adult per signature. Hawaii does not enforce a parent's pre-injury waiver
 * of a minor's claims, so the guardian option records supervision responsibility
 * and says so plainly instead of pretending to waive anything for a child.
 */
export default function WaiverPage() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const search = useSearch();
  const guestToken = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get("token");

  type WaiverContext = {
    document: LegalDocument;
    waiverUrl: string;
    signed: number;
    required: number;
    remaining: number;
    complete: boolean;
  };
  const contextUrl = `/api/legal/waiver-context/${bookingId}${guestToken ? `?token=${encodeURIComponent(guestToken)}` : ""}`;
  const { data: context, isLoading, isError, error: contextError, refetch: refetchContext } = useQuery<WaiverContext>({
    queryKey: [contextUrl],
    enabled: Boolean(bookingId),
    retry: false,
  });
  // The default fetcher throws "<status>: <body>", so the status is readable.
  const contextStatus = contextError instanceof Error ? parseInt(contextError.message, 10) : NaN;
  const bookingMissing = contextStatus === 404;

  // Asking for a fresh link. The server answers the same way whether or not
  // the booking exists, and the page says exactly that: no success is claimed
  // beyond "check the inbox on the booking".
  const [linkRequest, setLinkRequest] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  async function requestFreshLink() {
    if (!bookingId || linkRequest === "sending") return;
    setLinkRequest("sending");
    try {
      const res = await fetch(`/api/legal/waiver-link-request/${encodeURIComponent(bookingId)}`, { method: "POST" });
      setLinkRequest(res.ok ? "sent" : "failed");
    } catch {
      setLinkRequest("failed");
    }
  }
  const doc = context?.document;
  const progress = context;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [signature, setSignature] = useState("");
  const [role, setRole] = useState<"adult_participant" | "guardian">("adult_participant");
  // Children under 18 in this signer's care. Each one gets its own guardian
  // record, so the count against the party is honest and each child is named.
  const [children, setChildren] = useState<string[]>([]);
  const [childDraft, setChildDraft] = useState("");
  const [guardianAck, setGuardianAck] = useState(false);
  const [disclosureRead, setDisclosureRead] = useState(false);
  const [riskAck, setRiskAck] = useState(false);
  const [ableAck, setAbleAck] = useState(false);
  const [instructionAck, setInstructionAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ at: string; version: number } | null>(null);
  const [adultRecorded, setAdultRecorded] = useState(false);
  const [childrenRecorded, setChildrenRecorded] = useState<string[]>([]);

  const disclosureRef = useRef<HTMLDivElement>(null);

  function resetConsent() {
    if (disclosureRef.current) disclosureRef.current.scrollTop = 0;
    setDisclosureRead(false);
    setRiskAck(false);
    setAbleAck(false);
    setInstructionAck(false);
    setGuardianAck(false);
  }

  // Authorization and consent apply only to the exact document shown for this
  // exact booking link. Client-side route reuse must never carry them forward.
  useEffect(() => {
    resetConsent();
    setDone(null);
    setAdultRecorded(false);
    setChildrenRecorded([]);
  }, [bookingId, guestToken, doc?.contentHash]);

  useEffect(() => {
    setName("");
    setEmail("");
    setSignature("");
    setChildren([]);
    setChildDraft("");
    setRole("adult_participant");
    setError(null);
  }, [bookingId, guestToken]);

  const seoHead = useSEO({
    pageType: "waiver",
    title: "Water Activity Waiver | The Kualoa Beach House",
    description: "Sign the water activity waiver for your stay.",
    robots: "noindex, nofollow",
    referrer: "no-referrer",
  });

  // Enable the controls only once the disclosure has genuinely been read to the
  // end. Also fires when the panel is short enough not to scroll at all.
  useEffect(() => {
    const el = disclosureRef.current;
    if (!el || disclosureRead) return;
    const check = () => {
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) setDisclosureRead(true);
    };
    check();
    el.addEventListener("scroll", check, { passive: true });
    return () => el.removeEventListener("scroll", check);
  }, [doc, disclosureRead]);

  const disclosure = doc?.sections.find((s) => s.anchor === "inherent-risks");
  const instruction = doc?.sections.find((s) => s.anchor === "safety-instruction");
  const rest = doc?.sections.filter(
    (s) => s.anchor !== "inherent-risks" && s.anchor !== "safety-instruction",
  );

  const signatureMatches =
    signature.trim().length >= 2 &&
    signature.trim().toLowerCase() === name.trim().toLowerCase();

  const namedChildren = children.map((c) => c.trim()).filter((c) => c.length >= 2);
  const childrenOk = namedChildren.length === 0 || guardianAck;
  const selfOk = role === "guardian" ? namedChildren.length > 0 && guardianAck : riskAck && ableAck && instructionAck;
  const ready =
    Boolean(context && doc && context.waiverUrl) &&
    disclosureRead &&
    selfOk &&
    childrenOk &&
    name.trim().length >= 2 &&
    /\S+@\S+\.\S+/.test(email) &&
    signatureMatches &&
    !submitting;
  const disabledReason = !context
    ? "Open an authorized waiver link to sign."
    : !disclosureRead
      ? "Read the disclosure to the end to unlock signing."
      : name.trim().length < 2
        ? "Enter your full name."
        : !/\S+@\S+\.\S+/.test(email)
          ? "Enter a valid email address."
          : role === "guardian" && namedChildren.length === 0
            ? "Add at least one child."
            : !selfOk || !childrenOk
              ? "Complete the required acknowledgements."
              : !signatureMatches
                ? "Type your full name exactly as shown above."
                : submitting
                  ? "Your signature is being recorded."
                  : null;

  function addChild() {
    const v = childDraft.trim();
    if (v.length < 2) return;
    if (children.some((child) => child.trim().toLocaleLowerCase() === v.toLocaleLowerCase())) {
      setError("That child is already listed.");
      return;
    }
    setError(null);
    setChildren((prev) => [...prev, v]);
    setChildDraft("");
  }

  async function submit() {
    if (!context || !doc || !context.waiverUrl) {
      setError("This secure waiver link is not authorized. Open the newest booking email for a fresh link.");
      return;
    }
    setSubmitting(true);
    setError(null);
    const recorded: string[] = [];
    try {
      const base = {
        slug: "water_activity_waiver",
        bookingId: bookingId || null,
        token: guestToken || undefined,
        method: "emailed_link",
        signatureName: signature.trim(),
        expectedVersionNumber: doc.versionNumber,
        expectedContentHash: doc.contentHash,
      };
      let body: any = null;
      const accept = async (payload: Record<string, unknown>) => {
        const res = await fetch("/api/legal/accept", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });
        const responseBody = await res.json().catch(() => ({}));
        if (!res.ok) {
          const acceptanceError = new Error(responseBody.message || "We could not record this signature.") as Error & { status?: number; code?: string };
          acceptanceError.status = res.status;
          acceptanceError.code = responseBody.error_code;
          throw acceptanceError;
        }
        return responseBody;
      };
      if (role === "adult_participant" && !adultRecorded) {
        body = await accept({
          ...base,
          guestEmail: email.trim(),
          guestName: name.trim(),
          signerRole: "adult_participant",
          riskDisclosureAck: riskAck,
          physicallyAbleAck: ableAck,
          instructionAck: instructionAck,
        });
        setAdultRecorded(true);
        recorded.push(name.trim());
      }
      for (const child of namedChildren) {
        const childKey = child.toLocaleLowerCase();
        if (childrenRecorded.includes(childKey)) continue;
        const childBody = await accept({
          ...base,
          guestEmail: email.trim(),
          guestName: child,
          childName: child,
          guardianEmail: email.trim(),
          guardianName: name.trim(),
          signerRole: "guardian",
          partyConfirmed: true,
          riskDisclosureAck: guardianAck,
          physicallyAbleAck: guardianAck,
          instructionAck: guardianAck,
        });
        body = body ?? childBody;
        recorded.push(child);
        setChildrenRecorded((current) => current.includes(childKey) ? current : [...current, childKey]);
      }
      if (!body && adultRecorded) {
        body = { acceptedAt: new Date().toISOString(), versionNumber: doc.versionNumber };
      }
      setDone({ at: body.acceptedAt, version: body.versionNumber });
      await refetchContext();
    } catch (e) {
      await refetchContext();
      if (
        e instanceof Error
        && ((e as Error & { status?: number; code?: string }).status === 409
          || (e as Error & { status?: number; code?: string }).code === "VERSION_CHANGED")
      ) {
        resetConsent();
        setAdultRecorded(false);
        setChildrenRecorded([]);
        setError("The waiver was updated while you were signing. We loaded the current version. Please read it again, confirm the acknowledgements, and sign.");
        setSubmitting(false);
        return;
      }
      const reason = e instanceof Error ? e.message : "We could not record the signature.";
      setError(
        recorded.length
          ? `${recorded.join(", ")} ${recorded.length === 1 ? "was" : "were"} recorded, but the remaining signature was not. Your remaining details are still here. Please try again. ${reason}`
          : reason,
      );
      setSubmitting(false);
    }
  }

  function renderBody(body: string) {
    return body.split("\n").filter(Boolean).map((line, i) =>
      line.startsWith("- ") ? (
        <li key={i} className="flex gap-3 font-sans text-[15px] leading-relaxed text-ink-soft">
          <span className="shrink-0 text-driftwood">–</span>
          <span>{line.slice(2)}</span>
        </li>
      ) : (
        <p key={i} className="mb-3 font-sans text-[15px] leading-relaxed text-ink-soft">
          {line}
        </p>
      ),
    );
  }

  return (
    <>
      {seoHead}
      <div className="min-h-screen bg-shell pb-24 lg:pb-0">
        <Header />
        <main id="main-content">
          <ArticleMasthead
            eyebrow="Before you use the water equipment"
            title="Water Activity Waiver"
            deck="Kayaks, paddleboards and surfboards are optional and free to use. Everyone on the booking signs this online before arrival: adults for themselves, parents for their children. The equipment stays locked until every signature is in."
          />

          <div className="container mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-16">
            {isLoading && <p className="font-sans text-ink-soft">Loading the waiver…</p>}

            {isError && (
              <div className="border border-sand-deep bg-sand/50 p-6" data-testid="waiver-link-error">
                <h2 className="mb-2 font-serif text-luxury-xl text-forest">
                  {bookingMissing ? "We could not find a booking for this link." : "This waiver link has expired."}
                </h2>
                <p className="font-sans text-[15px] leading-relaxed text-ink-soft" role="alert">
                  {bookingMissing
                    ? "The address may have been shortened or mistyped when it was shared. Open the latest booking email and use the waiver link in it, or contact your host."
                    : "Links from older emails stop working once a booking's secure link is refreshed. The newest booking email has a working link, and the person who booked can share it from their stay page. Or ask for a fresh one here."}
                </p>
                {!bookingMissing && (
                  <div className="mt-5">
                    {linkRequest === "sent" ? (
                      <p className="font-sans text-[15px] leading-relaxed text-ink" role="status">
                        If this booking is upcoming, a fresh link is on its way to the email address on the booking. Check that inbox (and spam) in the next few minutes.
                      </p>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={requestFreshLink}
                          disabled={linkRequest === "sending"}
                          aria-busy={linkRequest === "sending" || undefined}
                          className="inline-flex min-h-[44px] items-center justify-center bg-sun px-6 font-sans text-[13px] font-semibold uppercase tracking-[0.08em] text-shell transition-colors hover:bg-sun-hover disabled:opacity-70"
                          data-testid="waiver-request-fresh-link"
                        >
                          {linkRequest === "sending" ? "Sending" : "Email a fresh link to the person who booked"}
                        </button>
                        {linkRequest === "failed" && (
                          <p className="mt-3 font-sans text-[14px] text-red-700" role="alert">
                            That did not go through. Please try again in a minute, or contact your host.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {done && (
              <div
                className="rounded-2xl border border-sand-deep bg-sand p-6 text-center sm:p-8"
                data-testid="waiver-signed"
              >
                <h2 className="mb-3 font-serif text-luxury-2xl font-normal text-forest">
                  Signed. Thank you.
                </h2>
                <p className="mx-auto max-w-[46ch] font-sans text-[15px] leading-relaxed text-ink-soft">
                  Recorded {new Date(done.at).toLocaleString("en-US")} against version{" "}
                  {done.version} of the waiver. A copy of exactly what you signed is kept and
                  is available to you on request.
                </p>
                <p className="mx-auto mt-5 max-w-[46ch] font-sans text-[14px] leading-relaxed text-driftwood">
                  {progress?.complete
                    ? "That was the last signature. The equipment is unlocked for your stay."
                    : progress
                      ? `${progress.signed} of ${progress.required} have now signed. Everyone else on the booking signs for themselves, so please pass this link on. The equipment stays locked until every signature is in.`
                      : "Everyone else on the booking signs for themselves, so please pass this link on. The equipment stays locked until every signature is in."}
                </p>
                {!progress?.complete && bookingId && (
                  context?.waiverUrl
                    ? <ShareWaiverLink url={context.waiverUrl} className="mx-auto mt-6 max-w-md text-left" />
                    : null
                )}
                <StayReturnLink bookingId={bookingId} className="mt-6" />
              </div>
            )}

            {progress && !done && (
              <div className="mb-10 rounded-2xl border border-sand-deep bg-sand/50 p-4 sm:p-5" data-testid="waiver-progress">
                <p className="font-sans text-[15px] leading-relaxed text-ink">
                  {progress.complete
                    ? "Everyone on this booking has signed. The equipment is unlocked for the stay."
                    : `${progress.signed} of ${progress.required} on this booking have signed. The kayaks, paddleboards and surfboards stay locked until every person has signed online.`}
                </p>
                {!progress.complete && bookingId && (
                  context?.waiverUrl ? <ShareWaiverLink url={context.waiverUrl} className="mt-4" /> : null
                )}
                <StayReturnLink bookingId={bookingId} className="mt-3" />
              </div>
            )}

            {doc && !done && (
              <div className="space-y-12">
                {/* Statutory disclosure. Must be read before anything else unlocks. */}
                {disclosure && (
                  <section>
                    <h2 className="mb-2 font-serif text-[clamp(20px,3vw,26px)] font-normal text-forest">
                      {disclosure.heading}
                    </h2>
                    <p className="mb-4 font-sans text-[13px] uppercase tracking-[0.2em] text-driftwood">
                      Read to the end to continue
                    </p>
                    <div
                      ref={disclosureRef}
                      className="max-h-80 overflow-y-auto overscroll-contain rounded-xl border border-sand-deep bg-white/60 p-4 sm:p-6"
                      data-testid="waiver-disclosure"
                    >
                      <ul className="space-y-3">{renderBody(disclosure.body)}</ul>
                    </div>
                    <p
                      className={`mt-3 font-sans text-[13px] ${disclosureRead ? "text-forest" : "text-driftwood"}`}
                      data-testid="waiver-disclosure-state"
                    >
                      {disclosureRead
                        ? "Disclosure read."
                        : "Scroll to the end of the disclosure to continue."}
                    </p>
                  </section>
                )}

                {instruction && (
                  <section>
                    <h2 className="mb-4 font-serif text-[clamp(20px,3vw,26px)] font-normal text-forest">
                      {instruction.heading}
                    </h2>
                    <div className="border border-sand-deep bg-sand/50 p-6">
                      <ul className="space-y-3">{renderBody(instruction.body)}</ul>
                    </div>
                  </section>
                )}

                {rest?.map((s) => (
                  <section key={s.heading}>
                    <h2 className="mb-4 font-serif text-[clamp(20px,3vw,26px)] font-normal text-forest">
                      {s.heading}
                    </h2>
                    <ul className="space-y-3">{renderBody(s.body)}</ul>
                  </section>
                ))}

                {/* Signing */}
                <section id="sign-section" className="scroll-mt-28 border-t border-sand-deep pt-10">
                  <h2 className="mb-6 font-serif text-[clamp(20px,3vw,26px)] font-normal text-forest">
                    Sign
                  </h2>

                  <fieldset
                    disabled={!disclosureRead}
                    className="min-w-0 space-y-6 disabled:opacity-50"
                    data-testid="waiver-fieldset"
                    aria-describedby="waiver-signing-status"
                  >
                    <div className="grid gap-5 sm:grid-cols-2">
                      <label className="block">
                        <span className="mb-2 block font-sans text-[12px] uppercase tracking-[0.2em] text-driftwood">
                          Your full name
                        </span>
                        <input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="w-full border border-sand-deep bg-shell min-h-[48px] px-4 py-3 font-sans text-[16px] text-ink focus:border-forest focus:outline-none"
                          data-testid="waiver-name"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-2 block font-sans text-[12px] uppercase tracking-[0.2em] text-driftwood">
                          Your email
                        </span>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full border border-sand-deep bg-shell min-h-[48px] px-4 py-3 font-sans text-[16px] text-ink focus:border-forest focus:outline-none"
                          data-testid="waiver-email"
                        />
                      </label>
                    </div>

                    <div className="space-y-3">
                      {[
                        {
                          key: "risk",
                          checked: riskAck,
                          set: setRiskAck,
                          testid: "waiver-ack-risk",
                          label:
                            "I have read the full disclosure of inherent risks above, and I accept those risks.",
                        },
                        {
                          key: "able",
                          checked: ableAck,
                          set: setAbleAck,
                          testid: "waiver-ack-able",
                          label:
                            "I can swim unassisted in open water, and no medical condition, medication, alcohol or drug makes these activities unsafe for me.",
                        },
                        {
                          key: "instruction",
                          checked: instructionAck,
                          set: setInstructionAck,
                          testid: "waiver-ack-instruction",
                          label:
                            "I have read the safety instruction and have had the chance to ask questions about it.",
                        },
                      ].map((c) => (
                        <label key={c.key} className="flex cursor-pointer items-start gap-3 rounded-xl border border-sand-deep bg-shell p-4 transition-colors has-[:checked]:border-forest/40 has-[:checked]:bg-sand/50">
                          <input
                            type="checkbox"
                            checked={c.checked}
                            onChange={(e) => c.set(e.target.checked)}
                            className="mt-0.5 h-5 w-5 shrink-0 accent-[#17311C]"
                            data-testid={c.testid}
                          />
                          <span className="font-sans text-[15px] leading-relaxed text-ink">
                            {c.label}
                          </span>
                        </label>
                      ))}
                    </div>

                    <div className="rounded-xl border border-sand-deep bg-sand/50 p-4 sm:p-5">
                      <p className="mb-3 font-sans text-[12px] uppercase tracking-[0.2em] text-driftwood">
                        I am signing as
                      </p>
                      <div className="space-y-2">
                        <label className="flex min-h-[44px] cursor-pointer items-start gap-3 py-1">
                          <input
                            type="radio"
                            name="signer-role"
                            checked={role === "adult_participant"}
                            onChange={() => setRole("adult_participant")}
                            className="mt-0.5 h-5 w-5 shrink-0 accent-[#17311C]"
                            data-testid="waiver-role-adult"
                          />
                          <span className="font-sans text-[15px] leading-relaxed text-ink">
                            I am 18 or older and may use the equipment myself.
                          </span>
                        </label>
                        <label className="flex min-h-[44px] cursor-pointer items-start gap-3 py-1">
                          <input
                            type="radio"
                            name="signer-role"
                            checked={role === "guardian"}
                            onChange={() => setRole("guardian")}
                            className="mt-0.5 h-5 w-5 shrink-0 accent-[#17311C]"
                            data-testid="waiver-role-guardian"
                          />
                          <span className="font-sans text-[15px] leading-relaxed text-ink">
                            I am a parent or guardian signing only for children in my care and will not use the equipment myself.
                          </span>
                        </label>
                      </div>
                      <p className="mt-4 font-sans text-[13px] leading-relaxed text-driftwood">
                        Adults sign for themselves. Children under 18 are added below and signed for by
                        their parent or guardian, who accepts responsibility for supervising them.
                      </p>
                    </div>

                    {/* Children in this signer's care */}
                    <div className="rounded-xl border border-sand-deep bg-sand/50 p-4 sm:p-5" data-testid="waiver-children">
                      <p className="mb-1 font-sans text-[12px] uppercase tracking-[0.2em] text-driftwood">
                        Children under 18 in my care
                      </p>
                      <p className="mb-3 font-sans text-[13px] leading-relaxed text-driftwood">
                        {role === "guardian" ? "Add each child you are signing for." : "Optional. Add each child who may use the equipment or enter the water with you."}
                      </p>
                      {namedChildren.length > 0 && (
                        <ul className="mb-3 divide-y divide-sand-deep border-y border-sand-deep">
                          {children.map((c, i) => (
                            <li key={c.trim().toLocaleLowerCase()} className="flex items-center justify-between gap-3 py-2">
                              <span className="font-sans text-[15px] text-ink">{c}</span>
                              <button
                                type="button"
                                onClick={() => setChildren((prev) => prev.filter((_, j) => j !== i))}
                                className="inline-flex min-h-[44px] items-center px-2 font-sans text-[12px] uppercase tracking-[0.16em] text-driftwood hover:text-ink"
                                aria-label={`Remove ${c}`}
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="flex gap-2">
                        <input
                          value={childDraft}
                          onChange={(e) => setChildDraft(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChild(); } }}
                          placeholder="Child's full name"
                          className="min-w-0 flex-1 border border-sand-deep bg-shell min-h-[48px] px-4 py-3 font-sans text-[16px] text-ink focus:border-forest focus:outline-none"
                          data-testid="waiver-child-name"
                        />
                        <button
                          type="button"
                          onClick={addChild}
                          className="min-h-[48px] shrink-0 border border-sand-deep bg-shell px-5 py-3 font-sans text-[12px] uppercase tracking-[0.16em] text-forest transition-colors hover:bg-sand"
                          data-testid="waiver-child-add"
                        >
                          Add
                        </button>
                      </div>
                      {(namedChildren.length > 0 || role === "guardian") && (
                        <label className="mt-4 flex cursor-pointer items-start gap-3">
                          <input
                            type="checkbox"
                            checked={guardianAck}
                            onChange={(e) => setGuardianAck(e.target.checked)}
                            className="mt-0.5 h-5 w-5 shrink-0 accent-[#17311C]"
                            data-testid="waiver-ack-guardian"
                          />
                          <span className="font-sans text-[15px] leading-relaxed text-ink">
                            I am the parent or legal guardian of the children named here. I have read the
                            disclosure of risks and the safety instruction on their behalf, I accept full
                            responsibility for supervising them in and near the water at all times, and I
                            agree to these terms for them to the fullest extent Hawaii law allows.
                          </span>
                        </label>
                      )}
                    </div>

                    <label className="block">
                      <span className="mb-2 block font-sans text-[12px] uppercase tracking-[0.2em] text-driftwood">
                        Type your full name to sign
                      </span>
                      <input
                        value={signature}
                        onChange={(e) => setSignature(e.target.value)}
                        placeholder={name || "Your full name"}
                        className="w-full border border-sand-deep bg-shell px-4 py-3 font-serif text-[20px] text-ink focus:border-forest focus:outline-none"
                        data-testid="waiver-signature"
                      />
                      {signature.trim().length > 0 && !signatureMatches && (
                        <span className="mt-2 block font-sans text-[13px] text-driftwood">
                          Your signature must match the name above.
                        </span>
                      )}
                    </label>
                  </fieldset>

                  {error && (
                    <p
                      className="mt-6 font-sans text-[14px] text-red-700"
                      role="alert"
                      data-testid="waiver-error"
                    >
                      {error}
                    </p>
                  )}

                  {!ready && disabledReason && (
                    <p id="waiver-signing-status" className="mt-6 font-sans text-[13px] leading-relaxed text-driftwood" role="status">
                      {disabledReason}
                    </p>
                  )}

                  <button
                    type="button"
                    disabled={!ready}
                    onClick={submit}
                    className="mt-8 inline-flex min-h-[52px] w-full items-center justify-center bg-sun px-8 py-4 font-sans text-[16px] tracking-wide text-shell transition-colors duration-300 hover:bg-sun-hover disabled:cursor-not-allowed disabled:bg-driftwood/40 disabled:text-shell/70 sm:w-auto"
                    data-testid="waiver-submit"
                  >
                    {submitting ? "Recording…" : "Sign the waiver"}
                  </button>

                  <p className="mt-5 font-sans text-[13px] leading-relaxed text-driftwood">
                    We record the date and time, your signature, and the exact text you
                    signed. Draft language, reviewed against Hawaii statute but not by a
                    licensed Hawaii attorney.
                  </p>
                </section>
              </div>
            )}
          </div>
        </main>
        {doc && !done && (
          <StickySignBar
            targetId="sign-section"
            title={disclosureRead ? "Ready to sign" : "Sign the waiver"}
            detail={disclosureRead ? "Your details and signature are at the end." : "Read the disclosure first, then sign at the end."}
            cta="Go to signing"
          />
        )}
        <Footer />
      </div>
    </>
  );
}
